import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import EmptyState from '@components/ui/EmptyState';
import TaskBoard from '@components/ui/TaskBoard';
import TaskModal from '@components/ui/TaskModal';
import './Dashboard.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiListResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

interface Person {
  id: string;
  full_name: string;
  email: string | null;
  status: string;
  company_name: string | null;
  updated_at: string;
}

interface Project {
  id: string;
  project_name: string;
  status: string;
  company_name: string | null;
  lead_person_name: string | null;
  estimated_close_date: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function personStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Customer':
      return 'success';
    case 'Opportunity':
    case 'Qualified':
      return 'warning';
    case 'Unqualified':
      return 'danger';
    default:
      return 'default';
  }
}

function projectStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Work Started':
    case 'Work Finished':
      return 'warning';
    default:
      return 'default';
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface PanelHeaderProps {
  title: string;
  count?: number;
  action?: React.ReactNode;
}

const PanelHeader: React.FC<PanelHeaderProps> = ({ title, count, action }) => (
  <div className="dashboard-panel-header">
    <div className="dashboard-panel-header-left">
      <h2 className="dashboard-panel-title">{title}</h2>
      {count !== undefined && (
        <span className="dashboard-panel-count">{count}</span>
      )}
    </div>
    {action && <div className="dashboard-panel-action">{action}</div>}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // Task modal state
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [taskBoardKey, setTaskBoardKey] = useState(0);

  // Prospects needing follow-up
  const [prospects, setProspects] = useState<Person[]>([]);
  const [prospectsLoading, setProspectsLoading] = useState(true);
  const [prospectsError, setProspectsError] = useState(false);

  // Active projects
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(false);

  // Recently added people

  // ─── Fetch data ─────────────────────────────────────────────────────────────

  const fetchProspects = useCallback(async () => {
    setProspectsLoading(true);
    setProspectsError(false);
    try {
      const params = new URLSearchParams({ status: 'Prospect', page_size: '10' });
      const [r1, r2] = await Promise.all([
        fetch(`/api/people?${params.toString()}`),
        fetch(`/api/people?status=Attempted+to+contact&page_size=10`),
      ]);
      if (!r1.ok || !r2.ok) throw new Error('fetch failed');
      const [j1, j2]: [ApiListResponse<Person>, ApiListResponse<Person>] = await Promise.all([
        r1.json() as Promise<ApiListResponse<Person>>,
        r2.json() as Promise<ApiListResponse<Person>>,
      ]);
      if (!j1.success || !j2.success) throw new Error('api failed');

      // Combine, sort by updated_at desc, keep top 8
      const combined: Person[] = [...j1.data, ...j2.data];
      combined.sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
      setProspects(combined.slice(0, 8));
    } catch {
      setProspectsError(true);
    } finally {
      setProspectsLoading(false);
    }
  }, []);

  const fetchActiveProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(false);
    try {
      const statuses = ['Proposal Accepted', 'Work Started', 'Work Finished'];
      const results: ApiListResponse<Project>[] = await Promise.all(
        statuses.map((s) =>
          fetch(`/api/projects?status=${encodeURIComponent(s)}&page_size=20`).then(
            (r) => r.json() as Promise<ApiListResponse<Project>>
          )
        )
      );
      if (results.some((r) => !r.success)) throw new Error('api failed');

      const combined: Project[] = results.flatMap((r) => r.data);
      // Sort: Work Started first, then Work Finished, then Proposal Accepted
      const ORDER: Record<string, number> = {
        'Work Started': 0,
        'Work Finished': 1,
        'Proposal Accepted': 2,
      };
      combined.sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9));
      setActiveProjects(combined);
    } catch {
      setProjectsError(true);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProspects();
    fetchActiveProjects();
  }, [fetchProspects, fetchActiveProjects]);

  // ─── Task handlers ──────────────────────────────────────────────────────────

  const handleCreateTask = () => {
    setEditingTaskId(null);
    setTaskModalOpen(true);
  };

  const handleEditTask = (taskId: string) => {
    setEditingTaskId(taskId);
    setTaskModalOpen(true);
  };

  const handleTaskSaved = () => {
    // Refresh the task board by changing its key
    setTaskBoardKey((prev) => prev + 1);
  };

  const handleCloseTaskModal = () => {
    setTaskModalOpen(false);
    setEditingTaskId(null);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="dashboard-page">
      <h1 className="dashboard-title">Dashboard</h1>

      <div className="dashboard-grid">

        {/* ── Panel: Prospects needing follow-up ─────────────────────────── */}
        <Card className="dashboard-panel">
          <PanelHeader
            title="Prospects needing follow-up"
            count={prospectsLoading ? undefined : prospects.length}
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/people')}>
                View all
              </Button>
            }
          />
          {prospectsLoading ? (
            <div className="dashboard-panel-loading">
              <LoadingSpinner size="sm" />
            </div>
          ) : prospectsError ? (
            <p className="dashboard-panel-error">
              Failed to load.{' '}
              <button className="dashboard-retry" onClick={fetchProspects}>Retry</button>
            </p>
          ) : prospects.length === 0 ? (
            <EmptyState
              title="No open prospects"
              message="All prospects have been followed up or progressed."
            />
          ) : (
            <div className="dashboard-item-list">
              {prospects.map((person) => (
                <button
                  key={person.id}
                  className="dashboard-item-row"
                  onClick={() => navigate(`/people/${person.id}`)}
                  type="button"
                >
                  <div className="dashboard-item-main">
                    <span className="dashboard-item-name">{person.full_name}</span>
                    <Badge variant={personStatusVariant(person.status)}>{person.status}</Badge>
                  </div>
                  {person.company_name && (
                    <span className="dashboard-item-sub">{person.company_name}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* ── Panel: Active projects ──────────────────────────────────────── */}
        <Card className="dashboard-panel">
          <PanelHeader
            title="Active projects"
            count={projectsLoading ? undefined : activeProjects.length}
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/projects')}>
                View all
              </Button>
            }
          />
          {projectsLoading ? (
            <div className="dashboard-panel-loading">
              <LoadingSpinner size="sm" />
            </div>
          ) : projectsError ? (
            <p className="dashboard-panel-error">
              Failed to load.{' '}
              <button className="dashboard-retry" onClick={fetchActiveProjects}>Retry</button>
            </p>
          ) : activeProjects.length === 0 ? (
            <EmptyState
              title="No active projects"
              message="Projects in progress will appear here."
            />
          ) : (
            <div className="dashboard-item-list">
              {activeProjects.map((project) => (
                <button
                  key={project.id}
                  className="dashboard-item-row"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  type="button"
                >
                  <div className="dashboard-item-main">
                    <span className="dashboard-item-name">{project.project_name}</span>
                    <Badge variant={projectStatusVariant(project.status)}>{project.status}</Badge>
                  </div>
                  <div className="dashboard-item-meta">
                    {project.company_name && (
                      <span className="dashboard-item-sub">{project.company_name}</span>
                    )}
                    {project.estimated_close_date && (
                      <span className="dashboard-item-date">
                        Due {formatDate(project.estimated_close_date)}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* ── Panel: Ops task board ──────────────────────────────────────── */}
        <Card className="dashboard-panel dashboard-panel--full-width">
          <PanelHeader title="Ops task board" />
          <TaskBoard
            key={taskBoardKey}
            onCreateTask={handleCreateTask}
            onEditTask={handleEditTask}
          />
        </Card>

      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={taskModalOpen}
        onClose={handleCloseTaskModal}
        onSave={handleTaskSaved}
        taskId={editingTaskId}
      />
    </div>
  );
};

export default Dashboard;